/**
 * 经营看板红黄绿状态语义（对应规划方案第七章预警体系）。
 *
 * 这里集中三件事，供所有展示组件复用：
 * 1. THRESHOLDS：各指标的绿/黄阈值标准，沉淀自规划 7.1；
 * 2. evaluateTone：把一个比率按阈值评成 green/amber/red/info；
 * 3. tone 到颜色/标签色的映射。
 *
 * 注意：阈值是「展示口径」，与具体数据源字段解耦。数据源确定后，
 * 只需把真实指标值喂进 evaluateTone，无需改这里的标准。
 */

const isNil = (value) => value === null || value === undefined;

export const TONE_HEX = {
    green: "#15a35a",
    amber: "#d98a0b",
    red: "#e2483d",
    info: "#2b6cff",
};

/**
 * 规划 7.1 主要预警指标阈值。
 * higherIsBetter=false 表示该指标越低越好（如拉货率、调机BH占比）。
 */
export const THRESHOLDS = {
    bhCompletion: { green: 0.95, amber: 0.85 },
    flightCompletion: { green: 0.95, amber: 0.9 },
    availability: { green: 0.95, amber: 0.9 },
    techReliability: { green: 0.98, amber: 0.95 },
    customerFulfillment: { green: 0.95, amber: 0.85 },
    customerExceptionRate: { green: 0.05, amber: 0.1, higherIsBetter: false },
    offloadRate: { green: 0.02, amber: 0.05, higherIsBetter: false },
    ferryBhShare: { green: 0.05, amber: 0.1, higherIsBetter: false },
    capacityLock1m: { green: 0.9, amber: 0.8 },
    capacityLock3m: { green: 0.85, amber: 0.7 },
    capacityLock6m: { green: 0.7, amber: 0.5 },
    cashReceivedRate: { green: 0.98, amber: 0.95 },
    fuelSurchargeRate: { green: 0.95, amber: 0.85 },
};

/**
 * 按时间窗口（天）选择对应的运力锁定率阈值。
 */
export const capacityLockThreshold = (horizonDays) => {
    const days = Number(horizonDays) || 0;
    if (days <= 31) return THRESHOLDS.capacityLock1m;
    if (days <= 93) return THRESHOLDS.capacityLock3m;
    return THRESHOLDS.capacityLock6m;
};

/**
 * 把一个比率评成色调。
 * @param {number} value 比率（0-1）
 * @param {{green:number, amber:number, higherIsBetter?:boolean}} thresholds
 * @returns {"green"|"amber"|"red"|"info"}
 */
export const evaluateTone = (value, thresholds) => {
    if (isNil(value) || Number.isNaN(Number(value)) || !thresholds) return "info";
    const v = Number(value);
    const { green, amber, higherIsBetter = true } = thresholds;
    if (higherIsBetter) {
        if (v >= green) return "green";
        if (v >= amber) return "amber";
        return "red";
    }
    if (v <= green) return "green";
    if (v <= amber) return "amber";
    return "red";
};

/** 兼容旧调用：rateTone(value, { green, amber }) */
export const rateTone = (value, thresholds) => evaluateTone(value, thresholds);

export const toneTagColor = (tone) =>
    tone === "green" ? "green" : tone === "amber" ? "gold" : tone === "red" ? "red" : "default";

export const toneHex = (tone) => TONE_HEX[tone] || TONE_HEX.info;

/** 风险等级（RED/YELLOW/GREEN）→ AntD Tag 颜色 */
export const riskColor = (level) => {
    const normalized = String(level || "").toUpperCase();
    if (normalized === "RED") return "red";
    if (normalized === "YELLOW" || normalized === "AMBER") return "gold";
    if (normalized === "GREEN") return "green";
    return "default";
};

/** 事件严重度 → AntD Tag 颜色 */
export const severityColor = (severity) => {
    const normalized = String(severity || "").toUpperCase();
    if (normalized === "ERROR" || normalized === "HIGH" || normalized === "RED") return "red";
    if (normalized === "WARNING" || normalized === "WARN" || normalized === "MEDIUM" || normalized === "YELLOW") return "gold";
    return "blue";
};

/** 数据质量等级 → AntD Tag 颜色 */
export const qualityColor = (level) => {
    if (level === "COMPLETE") return "green";
    if (level === "PARTIAL") return "gold";
    if (level === "MISSING") return "red";
    return "default";
};
